// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization, type Language } from '../../config/localization';
import Icon, { type IconName, ICONS } from '../ui/icons';
import { useSidebar } from '../../contexts/SidebarContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import ContextMenu from '../ui/ContextMenu';

export type ActiveSidebarContext = 'organization' | 'image' | 'multi' | 'gallery' | 'video' | 'audio' | 'text' | '3d' | 'settings' | 'help' | 'admin';

interface IconBarProps {
  selectedLanguage: Language;
  activeContext: ActiveSidebarContext;
  onContextChange: (context: ActiveSidebarContext) => void;
  className?: string;
}

interface IconButtonProps {
  iconName: IconName;
  context: ActiveSidebarContext;
  isActive: boolean;
  isDisabled?: boolean;
  title: string;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  href?: string;
}

const IconButton = ({ iconName, context, isActive, isDisabled = false, title, onClick, onContextMenu, href }: IconButtonProps) => {
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        onClick={onClick}
        onContextMenu={onContextMenu}
        onAuxClick={(e) => {
          if (e.button === 1 && href && !isDisabled) { // Middle click
            e.preventDefault();
            window.open(href, '_blank');
          }
        }}
        disabled={isDisabled}
        title={title}
        className={`
          w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-200
          ${isActive 
            ? 'bg-purple-600 text-white shadow-lg' 
            : isDisabled
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50'
              : 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white'
          }
          ${!isDisabled && 'hover:scale-105'}
        `}
      >
        <Icon 
          name={iconName} 
          size={24} 
          className="transition-colors duration-200" 
        />
      </button>
      <span className={`text-xs font-medium transition-colors duration-200 text-center leading-tight w-full px-1
        ${isActive 
          ? 'text-purple-300' 
          : isDisabled
            ? 'text-gray-600'
            : 'text-gray-400'
        }`}>
        {title}
      </span>
    </div>
  );
};

export default function IconBar({
  selectedLanguage,
  activeContext,
  onContextChange,
  className = ''
}: IconBarProps) {
  const navigate = useNavigate();
  const t = useLocalization(selectedLanguage);
  const { isContextPanelCollapsed, toggleContextPanel } = useSidebar();
  const { userAccess } = useOrganization();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; url: string } | null>(null);

  // Helper function to get URL for each context
  const getContextUrl = (context: ActiveSidebarContext): string => {
    switch(context) {
      case 'organization': return '/profile';
      case 'image': return '/image';
      case 'multi': return '/multi';
      case 'gallery': return '/gallery';
      case 'video': return '/video';
      case 'audio': return '/audio';
      case 'text': return '/text';
      case '3d': return '/3d';
      case 'admin': return '/admin';
      case 'settings': return '/settings';
      case 'help': return '/help';
      default: return '/';
    }
  };

  const mainIcons: Array<{
    context: ActiveSidebarContext;
    iconName: IconName;
    titleKey: keyof typeof t.iconBar;
    isDisabled?: boolean;
  }> = [
    {
      context: 'organization',
      iconName: ICONS.PROFILE,
      titleKey: 'profile'
    },
    {
      context: 'multi',
      iconName: ICONS.SPLIT_ARROWS,
      titleKey: 'multi'
    },
    {
      context: 'image',
      iconName: ICONS.IMAGE,
      titleKey: 'image'
    },
    {
      context: 'video',
      iconName: ICONS.VIDEO,
      titleKey: 'video'
    },
    {
      context: 'audio',
      iconName: ICONS.AUDIO,
      titleKey: 'audio'
    },
    {
      context: '3d',
      iconName: ICONS.CUBE_3D,
      titleKey: 'threeD'
    },
    {
      context: 'text',
      iconName: ICONS.TEXT,
      titleKey: 'text'
    },
    {
      context: 'gallery',
      iconName: ICONS.GALLERY,
      titleKey: 'gallery'
    }
  ];

  const systemIcons: Array<{
    context: ActiveSidebarContext;
    iconName: IconName;
    titleKey: keyof typeof t.iconBar;
  }> = [
    // Admin icon - only show for admins
    ...(userAccess?.isAdmin ? [{
      context: 'admin' as ActiveSidebarContext,
      iconName: ICONS.ADMIN,
      titleKey: 'admin' as keyof typeof t.iconBar
    }] : []),
    {
      context: 'settings',
      iconName: ICONS.SETTINGS,
      titleKey: 'settings'
    },
    {
      context: 'help',
      iconName: ICONS.HELP,
      titleKey: 'help'
    }
  ];

  return (
    // The bar is `fixed` + `h-full`, so anything taller than the viewport is
    // simply cut off — settings/help/version sit BELOW the generation icons and
    // are the first to go. Eight main icons (~67px each with their label) plus
    // the system group already need ~860px, more for admins, which no longer
    // fits a 1366×768 laptop. `overflow-y-auto` keeps the bottom controls
    // reachable at any height; the gaps only open up once there is room for
    // them, so the scroll is a fallback rather than the normal state.
    // `scrollbar-hide` keeps an 80px-wide nav rail from growing a scrollbar.
    <div className={`sidebar-mono fixed left-0 top-0 h-full w-20 bg-gray-900/95 backdrop-blur-md border-r border-gray-700/50 z-50 flex flex-col items-center py-4 overflow-y-auto overflow-x-hidden scrollbar-hide ${className}`}>
      {/* Main Icons — no separate Home button: the landing page is reachable
          from the header logo, and the slot was worth more as vertical space. */}
      <div className="flex flex-col gap-2 [@media(min-height:900px)]:gap-4 shrink-0">
        {mainIcons.map(({ context, iconName, titleKey, isDisabled }) => {
          const url = getContextUrl(context);
          return (
            <IconButton
              key={context}
              context={context}
              iconName={iconName}
              isActive={activeContext === context}
              isDisabled={isDisabled}
              title={t.iconBar[titleKey]}
              href={url}
              onClick={() => {
                console.log('🎯 ICON BAR CLICK:', { context, isDisabled, currentActive: activeContext });
                if (!isDisabled) {
                  // Навигация на соответствующие страницы
                  switch(context) {
                    case 'image':
                      navigate('/image');
                      break;
                    case 'multi':
                      navigate('/multi');
                      break;
                    case 'video':
                      navigate('/video');
                      break;
                    case 'audio':
                      navigate('/audio');
                      break;
                    case '3d':
                      navigate('/3d');
                      break;
                    case 'text':
                      navigate('/text');
                      break;
                    case 'gallery':
                      navigate('/gallery');
                      break;
                    case 'organization':
                      navigate('/profile');
                      break;
                    default:
                      onContextChange(context);
                  }
                }
              }}
              onContextMenu={(e) => {
                if (!isDisabled) {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, url });
                }
              }}
            />
          );
        })}
      </div>

      {/* Spacer — collapses to 0 when the bar overflows, so the system icons
          stay directly under the main group instead of being pushed further
          out of view. */}
      <div className="flex-1 shrink" />

      {/* System Icons */}
      <div className="flex flex-col gap-2 [@media(min-height:900px)]:gap-4 shrink-0">
        {systemIcons.map(({ context, iconName, titleKey }) => {
          const url = getContextUrl(context);
          return (
            <IconButton
              key={context}
              context={context}
              iconName={iconName}
              isActive={activeContext === context}
              title={t.iconBar[titleKey]}
              href={url}
              onClick={() => {
                console.log('🎯 SYSTEM ICON BAR CLICK:', { context, currentActive: activeContext });
                // Навигация для системных иконок
                switch(context) {
                  case 'settings':
                    navigate('/settings');
                    break;
                  case 'help':
                    navigate('/help');
                    break;
                  case 'admin':
                    navigate('/admin');
                    break;
                  default:
                    onContextChange(context);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setContextMenu({ x: e.clientX, y: e.clientY, url });
              }}
            />
          );
        })}
      </div>

      {/* Version indicator */}
      <div className="mt-2 [@media(min-height:900px)]:mt-4 shrink-0 text-xs text-gray-600 text-center leading-tight">
        v1.0
      </div>
      
      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={[
            {
              label: t.nav?.openInNewTab || 'Open in new tab',
              icon: '🔗',
              onClick: () => {
                window.open(contextMenu.url, '_blank');
              }
            }
          ]}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}